// frontend/shared-ui/src/components/report-cards/ReportCardDocument.jsx
import React from "react";

// ============================================
// Formatting helpers
// ============================================

const fmt = (v, decimals = 1) => {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
  return Number(v).toFixed(decimals);
};

const fmtDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

const TERM_LABEL = {
  term1: "Term 1",
  term2: "Term 2",
  annual: "Annual",
};

const TERM_LABEL_KU = {
  term1: "وەرزی یەکەم",
  term2: "وەرزی دووەم",
  annual: "ساڵانە",
};

// ============================================
// Sub-components
// ============================================

const SignatureLine = ({ label, name }) => (
  <div className="rc-signature">
    <div className="rc-sig-line" />
    <div className="rc-sig-name">{name || "\u00A0"}</div>
    <div className="rc-sig-label">{label}</div>
  </div>
);

// ============================================
// Main
// ============================================

/**
 * Props:
 *   - data             : the payload from /api/report-cards/preview or /get
 *   - comment          : optional string — overrides data.snapshot.comment
 *   - principalName    : optional string
 *   - homeroomTeacher  : optional string — overrides class.teacher.fullName
 *   - schoolName       : optional string (fallback: "School")
 *   - language         : 'en' | 'ku' (default 'en')
 *   - showWatermark    : boolean — show DRAFT watermark if the card is a draft
 */
const ReportCardDocument = ({
  data,
  comment,
  principalName,
  homeroomTeacher,
  schoolName = "School",
  language = "en",
  showWatermark = true,
}) => {
  if (!data) return null;

  const {
    student,
    classInfo,
    academicYear,
    term,
    subjectRows = [],
    attendanceSummary,
    examSummary,
    annualAverage,
    snapshot,
  } = data;

  const isDraft = !snapshot || snapshot.status !== "finalized";

  const termLabel = language === "ku" ? TERM_LABEL_KU[term] : TERM_LABEL[term];
  const labels =
    language === "ku"
      ? {
          reportCard: "کارتی ئەنجام",
          student: "قوتابی",
          studentCode: "کۆدی قوتابی",
          class: "پۆل",
          grade: "قۆناغ",
          academicYear: "ساڵی خوێندن",
          term: "وەرز",
          subject: "بابەت",
          term1: "وەرزی ١",
          term2: "وەرزی ٢",
          final: "کۆتایی",
          gradeLetter: "پلە",
          status: "دۆخ",
          passed: "دەرچووە",
          failed: "دەرنەچووە",
          notGraded: "—",
          attendance: "ئامادەبوون",
          totalDays: "کۆی ڕۆژەکان",
          present: "ئامادە",
          absent: "ئامادەنەبوو",
          late: "درەنگ",
          excused: "مۆڵەتدراو",
          attendancePercent: "ڕێژەی ئامادەبوون",
          examSummary: "پوختەی تاقیکردنەوەکان",
          examsTaken: "ژمارەی تاقیکردنەوەکان",
          average: "تێکڕا",
          best: "باشترین",
          worst: "خراپترین",
          annualAverage: "تێکڕای ساڵانە",
          comment: "تێبینی مامۆستا",
          homeroomTeacher: "مامۆستای پۆل",
          principal: "بەڕێوەبەر",
          signature: "واژوو",
          finalizedOn: "کۆتایی پێهێنرا لە",
          draft: "ڕەشنووس",
          page: "لاپەڕە",
          noSubjects: "هیچ بابەتێک تۆمار نەکراوە",
        }
      : {
          reportCard: "Report Card",
          student: "Student",
          studentCode: "Student ID",
          class: "Class",
          grade: "Grade",
          academicYear: "Academic Year",
          term: "Term",
          subject: "Subject",
          term1: "Term 1",
          term2: "Term 2",
          final: "Final",
          gradeLetter: "Grade",
          status: "Status",
          passed: "Passed",
          failed: "Failed",
          notGraded: "—",
          attendance: "Attendance",
          totalDays: "Total Days",
          present: "Present",
          absent: "Absent",
          late: "Late",
          excused: "Excused",
          attendancePercent: "Attendance %",
          examSummary: "Exam Summary",
          examsTaken: "Exams Taken",
          average: "Average",
          best: "Best Subject",
          worst: "Weakest Subject",
          annualAverage: "Annual Average",
          comment: "Teacher's Comment",
          homeroomTeacher: "Homeroom Teacher",
          principal: "Principal",
          signature: "Signature",
          finalizedOn: "Finalized on",
          draft: "DRAFT",
          page: "Page",
          noSubjects: "No subjects recorded",
        };

  const finalComment =
    comment !== undefined && comment !== null
      ? comment
      : snapshot?.comment || "";

  const finalPrincipal = principalName || snapshot?.principalName || "";

  const finalHomeroomTeacher =
    homeroomTeacher || classInfo?.teacher?.fullName || "";

  return (
    <div
      className={`rc-document ${showWatermark && isDraft ? "is-draft" : ""}`}
    >
      {showWatermark && isDraft && (
        <div className="rc-watermark">{labels.draft}</div>
      )}

      {/* -------- Header -------- */}
      <header className="rc-header">
        <div className="rc-header-school">
          <div className="rc-school-name">{schoolName}</div>
          <div className="rc-school-sub">
            {labels.academicYear}: {academicYear} · {labels.term}: {termLabel}
          </div>
        </div>
        <div className="rc-header-title">
          <h1>{labels.reportCard}</h1>
        </div>
      </header>

      {/* -------- Student info block -------- */}
      <section className="rc-info-grid">
        <div className="rc-info-item">
          <span className="rc-info-label">{labels.student}</span>
          <span className="rc-info-value">{student?.fullName || "—"}</span>
        </div>
        <div className="rc-info-item">
          <span className="rc-info-label">{labels.studentCode}</span>
          <span className="rc-info-value">{student?.studentId || "—"}</span>
        </div>
        <div className="rc-info-item">
          <span className="rc-info-label">{labels.class}</span>
          <span className="rc-info-value">
            {classInfo
              ? `${classInfo.name}${classInfo.section ? `-${classInfo.section}` : ""}`
              : "—"}
          </span>
        </div>
        <div className="rc-info-item">
          <span className="rc-info-label">{labels.grade}</span>
          <span className="rc-info-value">
            {classInfo?.gradeName || classInfo?.gradeLevel || "—"}
          </span>
        </div>
      </section>

      {/* -------- Annual average highlight -------- */}
      <section className="rc-highlight">
        <div className="rc-highlight-value">{fmt(annualAverage)}</div>
        <div className="rc-highlight-label">{labels.annualAverage}</div>
      </section>

      {/* -------- Subject table -------- */}
      <section className="rc-section">
        <h2 className="rc-section-title">{labels.subject}</h2>
        {subjectRows.length === 0 ? (
          <div className="rc-empty">{labels.noSubjects}</div>
        ) : (
          <table className="rc-table">
            <thead>
              <tr>
                <th>{labels.subject}</th>
                <th className="rc-col-num">{labels.term1}</th>
                <th className="rc-col-num">{labels.term2}</th>
                <th className="rc-col-num">{labels.final}</th>
                <th className="rc-col-letter">{labels.gradeLetter}</th>
                <th className="rc-col-status">{labels.status}</th>
              </tr>
            </thead>
            <tbody>
              {subjectRows.map((s, i) => (
                <tr
                  key={s.subjectId || i}
                  className={s.isPassed === false ? "rc-row-fail" : ""}
                >
                  <td>
                    <div className="rc-subject-cell">
                      <span
                        className="rc-subject-dot"
                        style={{ background: s.color || "#f97316" }}
                      >
                        {s.icon || "📖"}
                      </span>
                      <span className="rc-subject-name">{s.name}</span>
                      <span className="rc-subject-code">{s.code}</span>
                    </div>
                  </td>
                  <td className="rc-col-num">{fmt(s.term1)}</td>
                  <td className="rc-col-num">{fmt(s.term2)}</td>
                  <td className="rc-col-num rc-col-bold">
                    {fmt(
                      term === "annual"
                        ? s.final
                        : term === "term1"
                          ? s.term1
                          : s.term2,
                    )}
                  </td>
                  <td className="rc-col-letter">
                    {s.gradeLetter || labels.notGraded}
                  </td>
                  <td className="rc-col-status">
                    {s.isPassed === true ? (
                      <span className="rc-status rc-status-pass">
                        {labels.passed}
                      </span>
                    ) : s.isPassed === false ? (
                      <span className="rc-status rc-status-fail">
                        {labels.failed}
                      </span>
                    ) : (
                      <span className="rc-status rc-status-na">
                        {labels.notGraded}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* -------- Two-column summaries -------- */}
      <section className="rc-two-col">
        {/* Attendance */}
        {attendanceSummary && (
          <div className="rc-section">
            <h2 className="rc-section-title">{labels.attendance}</h2>
            <div className="rc-stats-grid">
              <div className="rc-stat">
                <span className="rc-stat-value">{attendanceSummary.total}</span>
                <span className="rc-stat-label">{labels.totalDays}</span>
              </div>
              <div className="rc-stat rc-stat-success">
                <span className="rc-stat-value">
                  {attendanceSummary.present}
                </span>
                <span className="rc-stat-label">{labels.present}</span>
              </div>
              <div className="rc-stat rc-stat-danger">
                <span className="rc-stat-value">
                  {attendanceSummary.absent}
                </span>
                <span className="rc-stat-label">{labels.absent}</span>
              </div>
              <div className="rc-stat rc-stat-warning">
                <span className="rc-stat-value">{attendanceSummary.late}</span>
                <span className="rc-stat-label">{labels.late}</span>
              </div>
              <div className="rc-stat rc-stat-info">
                <span className="rc-stat-value">
                  {attendanceSummary.excused}
                </span>
                <span className="rc-stat-label">{labels.excused}</span>
              </div>
              <div className="rc-stat rc-stat-primary">
                <span className="rc-stat-value">
                  {fmt(attendanceSummary.percentage)}%
                </span>
                <span className="rc-stat-label">
                  {labels.attendancePercent}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Exam summary */}
        {examSummary && examSummary.count > 0 && (
          <div className="rc-section">
            <h2 className="rc-section-title">{labels.examSummary}</h2>
            <div className="rc-exam-list">
              <div className="rc-exam-row">
                <span>{labels.examsTaken}</span>
                <strong>{examSummary.count}</strong>
              </div>
              <div className="rc-exam-row">
                <span>{labels.average}</span>
                <strong>{fmt(examSummary.average)}%</strong>
              </div>
              {examSummary.best && (
                <div className="rc-exam-row rc-exam-best">
                  <span>{labels.best}</span>
                  <strong>
                    {examSummary.best.subject} · {fmt(examSummary.best.pct)}%
                  </strong>
                </div>
              )}
              {examSummary.worst && (
                <div className="rc-exam-row rc-exam-worst">
                  <span>{labels.worst}</span>
                  <strong>
                    {examSummary.worst.subject} · {fmt(examSummary.worst.pct)}%
                  </strong>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* -------- Teacher comment -------- */}
      {finalComment && (
        <section className="rc-section rc-comment">
          <h2 className="rc-section-title">{labels.comment}</h2>
          <div className="rc-comment-body">{finalComment}</div>
        </section>
      )}

      {/* -------- Signatures -------- */}
      <section className="rc-signatures">
        <SignatureLine
          label={labels.homeroomTeacher}
          name={finalHomeroomTeacher}
        />
        <SignatureLine label={labels.principal} name={finalPrincipal} />
      </section>

      {/* -------- Footer -------- */}
      <footer className="rc-footer">
        {snapshot?.status === "finalized" && snapshot.finalizedAt && (
          <span>
            {labels.finalizedOn} {fmtDate(snapshot.finalizedAt)}
          </span>
        )}
        <span className="rc-footer-page">
          {schoolName} · {academicYear}
        </span>
      </footer>
    </div>
  );
};

export { ReportCardDocument };
// export default ReportCardDocument;
